import { useState, useEffect } from 'react';
import { FiCheck, FiX, FiUser, FiCalendar, FiChevronLeft, FiChevronRight, FiFilter } from 'react-icons/fi';
import clsx from 'clsx';
import { getTodayLocal, getTodayFormatted, formatDate } from '../../utils/helpers';
import { ordersAPI } from '../../services/api';

const RoomStatusOverview = ({ rooms, orders, users, selectedDate, onDateChange }) => {
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [localOrders, setLocalOrders] = useState(orders);
  const [updatingOrders, setUpdatingOrders] = useState(new Set());
  // Get today's date
  const today = getTodayLocal();

  // Sync localOrders when orders prop changes (from WebSocket updates)
  useEffect(() => {
    setLocalOrders(orders);
  }, [orders]);

  // Handler for checkout status change
  const handleCheckoutToggle = async (orderId, isCurrentlyCompleted) => {
    try {
      setUpdatingOrders(prev => new Set(prev).add(orderId));

      // If currently completed, unmark it; otherwise mark it
      if (isCurrentlyCompleted) {
        await ordersAPI.unmarkCheckout(orderId);

        // Update local state
        setLocalOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId
              ? { ...order, checkout_timestamp: null }
              : order
          )
        );
      } else {
        await ordersAPI.markCheckout(orderId);

        // Update local state
        setLocalOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId
              ? { ...order, checkout_timestamp: new Date().toISOString() }
              : order
          )
        );
      }
    } catch (error) {
      console.error('Error updating checkout status:', error);
    } finally {
      setUpdatingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Filter orders for selected date and create room-order mapping
  const displayDate = selectedDate || today;
  const todayOrders = localOrders.filter(order => order.date === displayDate);
  const roomOrderMap = {};

  todayOrders.forEach(order => {
    if (!roomOrderMap[order.room_id]) {
      roomOrderMap[order.room_id] = [];
    }
    roomOrderMap[order.room_id].push(order);
  });

  // Get rooms with orders and sort by room number
  const roomsWithOrders = rooms
    .filter(room => roomOrderMap[room.id])
    .sort((a, b) => {
      // Convert room numbers to integers for proper sorting
      const numA = parseInt(a.number) || 0;
      const numB = parseInt(b.number) || 0;
      return numA - numB;
    });

  const getOrderStatus = (order) => {
    const checkoutDone = order.checkout_timestamp !== null;
    const cleaningDone = order.cleaning_timestamp !== null;

    if (checkoutDone && cleaningDone) {
      return 'completed';
    } else if (checkoutDone || cleaningDone) {
      return 'in_progress';
    } else {
      return 'pending';
    }
  };

  const getStaffName = (staffId) => {
    if (!staffId) return null;
    const staff = users.find(u => u.id === staffId);
    return staff?.name || null;
  };

  const isToday = displayDate === getTodayFormatted();

  // Función para extraer el número de planta del número de habitación
  const getFloorFromRoomNumber = (roomNumber) => {
    if (!roomNumber) return null;
    const match = roomNumber.toString().match(/^(\d+)/);
    if (match) {
      const digits = match[1];
      if (digits.length <= 3) {
        return digits.charAt(0);
      } else {
        return digits.substring(0, 2);
      }
    }
    return null;
  };

  // Obtener lista única de plantas que tienen órdenes de limpieza
  const availableFloors = [...new Set(
    roomsWithOrders
      .map(room => getFloorFromRoomNumber(room.number))
      .filter(floor => floor !== null)
  )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  // Filtrar habitaciones por planta
  const filteredRoomsWithOrders = selectedFloor === 'all'
    ? roomsWithOrders
    : roomsWithOrders.filter(room => {
        const floor = getFloorFromRoomNumber(room.number);
        return floor === selectedFloor;
      });

  const handleDateChange = (e) => {
    if (onDateChange) {
      onDateChange(e.target.value);
    }
  };

  const handlePreviousDay = () => {
    if (onDateChange) {
      const [year, month, day] = displayDate.split('-').map(Number);
      const currentDate = new Date(year, month - 1, day);
      currentDate.setDate(currentDate.getDate() - 1);

      const newYear = currentDate.getFullYear();
      const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      const newDay = String(currentDate.getDate()).padStart(2, '0');
      const newDate = `${newYear}-${newMonth}-${newDay}`;

      onDateChange(newDate);
    }
  };

  const handleNextDay = () => {
    if (onDateChange) {
      const [year, month, day] = displayDate.split('-').map(Number);
      const currentDate = new Date(year, month - 1, day);
      currentDate.setDate(currentDate.getDate() + 1);

      const newYear = currentDate.getFullYear();
      const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      const newDay = String(currentDate.getDate()).padStart(2, '0');
      const newDate = `${newYear}-${newMonth}-${newDay}`;

      onDateChange(newDate);
    }
  };

  const handleTodayClick = () => {
    if (onDateChange) {
      onDateChange(getTodayFormatted());
    }
  };

  if (roomsWithOrders.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {/* Header con selector de fecha y filtro de plantas */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Estado de Habitaciones
          </h3>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Date Selector */}
            {onDateChange && (
              <div className="flex items-center gap-2">
                <FiCalendar className="text-gray-500" aria-hidden="true" />
                <label htmlFor="date-selector-rooms" className="text-sm font-medium text-gray-700">
                  Fecha:
                </label>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePreviousDay}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                    aria-label="Día anterior"
                    title="Día anterior"
                  >
                    <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>

                  <input
                    id="date-selector-rooms"
                    type="date"
                    value={displayDate}
                    onChange={handleDateChange}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />

                  <button
                    onClick={handleNextDay}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                    aria-label="Día siguiente"
                    title="Día siguiente"
                  >
                    <FiChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                {!isToday && (
                  <button
                    onClick={handleTodayClick}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    Hoy
                  </button>
                )}
              </div>
            )}

            {/* Floor Filter */}
            <div className="flex items-center gap-2">
              <FiFilter className="text-gray-500" aria-hidden="true" />
              <select
                value={selectedFloor}
                onChange={(e) => setSelectedFloor(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                disabled={availableFloors.length === 0}
              >
                <option value="all">Todas las plantas</option>
                {availableFloors.map(floor => (
                  <option key={floor} value={floor}>
                    Planta {floor}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="text-center text-gray-500 py-4">
          No hay órdenes de limpieza para {isToday ? 'hoy' : formatDate(new Date(displayDate))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header con selector de fecha y filtro de plantas */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="text-lg font-semibold text-gray-900">
          Estado de Habitaciones - {isToday ? 'Hoy' : formatDate(new Date(displayDate))} ({filteredRoomsWithOrders.length})
        </h3>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Selector */}
          {onDateChange && (
            <div className="flex items-center gap-2">
              <FiCalendar className="text-gray-500" aria-hidden="true" />
              <label htmlFor="date-selector-rooms-main" className="text-sm font-medium text-gray-700">
                Fecha:
              </label>
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePreviousDay}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Día anterior"
                  title="Día anterior"
                >
                  <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>

                <input
                  id="date-selector-rooms-main"
                  type="date"
                  value={displayDate}
                  onChange={handleDateChange}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                <button
                  onClick={handleNextDay}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Día siguiente"
                  title="Día siguiente"
                >
                  <FiChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              {!isToday && (
                <button
                  onClick={handleTodayClick}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                >
                  Hoy
                </button>
              )}
            </div>
          )}

          {/* Floor Filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-500" aria-hidden="true" />
            <select
              value={selectedFloor}
              onChange={(e) => setSelectedFloor(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              disabled={availableFloors.length === 0}
            >
              <option value="all">Todas las plantas</option>
              {availableFloors.map(floor => (
                <option key={floor} value={floor}>
                  Planta {floor}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {filteredRoomsWithOrders.map(room => {
          const roomOrders = roomOrderMap[room.id];
          const mainOrder = roomOrders[0]; // Assuming one order per room per day
          const status = getOrderStatus(mainOrder);
          const staffName = getStaffName(mainOrder.cleaning_staff_id);
          const checkoutDone = mainOrder.checkout_timestamp !== null;
          const cleaningDone = mainOrder.cleaning_timestamp !== null;

          return (
            <div
              key={room.id}
              className={clsx(
                'border rounded-lg p-3 transition-colors',
                {
                  'border-yellow-200 bg-yellow-50': status === 'pending',
                  'border-blue-200 bg-blue-50': status === 'in_progress',
                  'border-green-200 bg-green-50': status === 'completed',
                }
              )}
            >
              {/* Room number and status */}
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-sm text-gray-900">
                  {room.number}
                </div>
                <div className={clsx(
                  'w-3 h-3 rounded-full',
                  {
                    'bg-yellow-400': status === 'pending',
                    'bg-blue-400': status === 'in_progress',
                    'bg-green-400': status === 'completed',
                  }
                )} />
              </div>

              {/* Staff assignment */}
              <div className="mb-2">
                {staffName ? (
                  <div className="flex items-center space-x-1 text-xs text-gray-600">
                    <FiUser className="h-3 w-3" aria-hidden="true" />
                    <span className="truncate">{staffName}</span>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400">Sin asignar</div>
                )}
              </div>

              {/* Status indicators */}
              <div className="space-y-2">
                {/* Checkout toggle slider */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCheckoutToggle(mainOrder.id, checkoutDone)}
                    disabled={updatingOrders.has(mainOrder.id)}
                    className={clsx(
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1',
                      {
                        'bg-green-500 focus:ring-green-500': checkoutDone,
                        'bg-red-500 focus:ring-red-500': !checkoutDone,
                        'opacity-50 cursor-not-allowed': updatingOrders.has(mainOrder.id)
                      }
                    )}
                    title={updatingOrders.has(mainOrder.id) ? 'Actualizando...' : (checkoutDone ? 'Check-out realizado' : 'Check-out pendiente')}
                  >
                    <span
                      className={clsx(
                        'inline-block h-3 w-3 transform rounded-full bg-white transition-transform',
                        {
                          'translate-x-5': checkoutDone,
                          'translate-x-1': !checkoutDone
                        }
                      )}
                    />
                  </button>
                  <span className="text-xs text-gray-600">Checkout</span>
                </div>

                {/* Cleaning indicator (read-only) */}
                <div className="flex items-center space-x-2">
                  <div className={clsx(
                    'flex items-center justify-center w-5 h-5 rounded-full',
                    {
                      'bg-green-500': cleaningDone,
                      'bg-gray-300': !cleaningDone,
                    }
                  )} title={cleaningDone ? 'Limpieza completada' : 'Limpieza pendiente'}>
                    {cleaningDone ? (
                      <FiCheck className="h-3 w-3 text-white" aria-hidden="true" />
                    ) : (
                      <FiX className="h-3 w-3 text-gray-600" aria-hidden="true" />
                    )}
                  </div>
                  <span className="text-xs text-gray-600">Limpieza</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <span>Pendiente</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-blue-400"></div>
            <span>En Progreso</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
            <span>Completada</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="relative inline-flex h-4 w-7 items-center rounded-full bg-green-500">
              <span className="inline-block h-2 w-2 transform rounded-full bg-white translate-x-4" />
            </div>
            <span>Checkout (Click para cambiar)</span>
          </div>
          <div className="flex items-center space-x-1">
            <FiCheck className="h-3 w-3 text-green-500" aria-hidden="true" />
            <span>Limpieza Completada</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomStatusOverview;